<template>
  <SectionHeader
    :show-content="defaultBidSettingsShowContents"
    :widget-title="$t('HEADER_DEFAULT_BID_SETTINGS')"
    @onShowContentsClick="onDefaultBidSettingsShowContentsClick()"
  >
    <template #content>
      <form
        role="form"
        class="form-horizontal"
        name="vm.campaignForm"
      >
        <div v-if="overridesAvailable">
          <Field
            :label="$t('LABEL_OVERRIDE_DEFAULT_BID_SETTINGS')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_OVERRIDE_DEFAULT_BID_SETTINGS')"
          >
            <template #element>
              <div class="p-l-0 col-sm-5 col-md-1">
                <ValidationProvider
                  rules="required"
                  slim
                >
                  <nzToggle
                    v-model="overrideDefaultBidSettings"
                    data-cy="default-bid-settings-overide-toggle"
                    @onToggle="overrideDefaultBidSettingsToggle"
                  />
                </ValidationProvider>
              </div>
            </template>
          </Field>
        </div>

        <div v-if="!overridesAvailable || overrideDefaultBidSettings">
          <Field
            :label="$t('LABEL_BASE_BID_AMOUNT')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_BASE_BID_AMOUNT')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes, errors }"
                :rules="passThroughBidEnabled ? '' : 'required|min_value:0'"
                slim
              >
                <div
                  class="p-l-0 col-sm-5 col-md-1"
                  :class="{'display-none': passThroughBidEnabled}"
                >
                  <input
                    :value="baseBidAmount"
                    name="currentBaseBidAmount"
                    data-cy="currentBaseBidAmount"
                    :required="passThroughBidEnabled ? false : true"
                    :placeholder="$t('LABEL_PLACEHOLDER_BASE_BID_AMOUNT')"
                    class="form-control dynamic-inbound-calls-basebid-and-maxBidAmount-input"
                    :class="classes"
                    :type="baseBidNumericInput ? 'number' : 'text'"
                    :disabled="passThroughBidEnabled"
                    @blur="currentBaseBidAmount = currentBaseBidAmount ? ('$' + currentBaseBidAmount) : ''; baseBidNumericInput = false"
                    @focus="currentBaseBidAmount = currentBaseBidAmount.replace('$',''); baseBidNumericInput = true"
                    @input="onBaseBidAmountOnInput"
                    @change="onBaseBidAmountOnChange"
                  >
                </div>
                <label
                  v-if="errors[0] && passThroughBidEnabledAllowErrors"
                  name="currentBaseBidAmount-invalid-label"
                  data-cy="currentBaseBidAmount-invalid-label"
                  class="form-group__label-required vv-invalid"
                >{{ $t('ERROR_BASE_BID_AMOUNT') }}
                </label>
                <label
                  v-else
                  class="form-group__label-required"
                >{{ $t('REQUIRED') }}
                </label>
                <label
                  v-if="passThroughBidEnabled"
                  name="currentBaseBidAmount-disabled-label"
                  data-cy="currentBaseBidAmount-disabled-label"
                  class="p-t-7 text-muted"
                >{{ $t('LABEL_BID_SETTINGS_OVERRIDE') }}
                </label>
              </ValidationProvider>
            </template>
          </Field>

          <Field
            :label="$t('LABEL_MAX_BID')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_MAX_BID')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes, errors }"
                :rules="passThroughBidEnabled ? '' : 'required|min_value:0.01'"
                slim
              >
                <div
                  class="p-l-0 col-sm-5 col-md-1"
                  :class="{'display-none': passThroughBidEnabled}"
                >
                  <input
                    :value="maxBidAmount"
                    name="currentmaxBidAmount"
                    data-cy="currentmaxBidAmount"
                    :required="passThroughBidEnabled ? false : true"
                    :placeholder="$t('LABEL_PLACEHOLDER_MAX_BID')"
                    class="form-control dynamic-inbound-calls-basebid-and-maxBidAmount-input"
                    :class="classes"
                    :type="maxBidAmountNumericInput ? 'number' : 'text'"
                    :disabled="passThroughBidEnabled"
                    @blur="currentmaxBidAmount = currentmaxBidAmount ? '$' + currentmaxBidAmount : ''; maxBidAmountNumericInput = false"
                    @focus="currentmaxBidAmount = currentmaxBidAmount.replace('$',''); maxBidAmountNumericInput = true"
                    @input="onmaxBidAmountOnInput"
                    @change="onmaxBidAmountOnChange"
                  >
                </div>
                <label
                  v-if="errors[0] && passThroughBidEnabledAllowErrors"
                  name="currentmaxBidAmount-invalid-label"
                  data-cy="currentmaxBidAmount-invalid-label"
                  class="form-group__label-required vv-invalid"
                >{{ $t('ERROR_MAX_BID_AMOUNT') }}
                </label>
                <label
                  v-else
                  class="form-group__label-required"
                >{{ $t('REQUIRED') }}
                </label>
                <label
                  v-if="passThroughBidEnabled"
                  name="currentBaseBidAmount-disabled-label"
                  data-cy="currentBaseBidAmount-disabled-label"
                  class="p-t-7 text-muted"
                >{{ $t('LABEL_BID_SETTINGS_OVERRIDE') }}
                </label>
              </ValidationProvider>
            </template>
          </Field>
          
          <Field
            :label="$t('LABEL_MIN_BID')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_MIN_BID')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes, errors }"
                :rules="passThroughBidEnabled ? '' : 'min_value:0.01'"
                slim
              >
                <div 
                  v-if="!passThroughBidEnabled" 
                  class="p-l-0 col-sm-5 col-md-1"
                >
                  <input
                    :value="minBidAmount"
                    name="currentminBidAmount"
                    :placeholder="$t('LABEL_PLACEHOLDER_MIN_BID')"
                    class="form-control dynamic-inbound-calls-basebid-and-minBidAmount-input"
                    :class="classes"
                    :type="minBidAmountNumericInput ? 'number' : 'text'"
                    :disabled="passThroughBidEnabled"
                    @blur="currentminBidAmount = currentminBidAmount ? '$' + currentminBidAmount : ''; minBidAmountNumericInput = false"
                    @focus="currentminBidAmount = currentminBidAmount.replace('$',''); minBidAmountNumericInput = true"
                    @input="onminBidAmountOnInput"
                    @change="onminBidAmountOnChange"
                  >
                </div>
                <label
                  v-if="errors[0] && passThroughBidEnabledAllowErrors"
                  name="currentminBidAmount-invalid-label"
                  class="form-group__label-required vv-invalid"
                >{{ $t('ERROR_MIN_BID_AMOUNT') }}
                </label>
                <label
                  v-if="passThroughBidEnabled"
                  name="currentBaseBidAmount-disabled-label"
                  data-cy="currentBaseBidAmount-disabled-label"
                  class="p-t-7 text-muted"
                >{{ $t('LABEL_BID_SETTINGS_OVERRIDE') }}
                </label>
              </ValidationProvider>
            </template>
          </Field>

          <Field
            :label="$t('LABEL_PAYOUT_ON')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_PAYOUT_ON')"
          >
            <template #element>
              <ValidationProvider
                v-slot="{ classes }"
                :rules="passThroughBidEnabled ? '' : 'required'"
                slim
              >
                <div 
                  v-if="!passThroughBidEnabled"
                  class="p-l-0 col-sm-4 col-md-3"
                >
                  <SearchableDropdown
                    v-model="currentConversionType"
                    name="conversionType"
                    data-cy="conversionType"
                    class="rtb-default-selector m-b-10"
                    :class="classes"
                    :config="conversionTypeDropdownConfig"
                    :searchable="false"
                    :clearable="false"
                    @change="onPayoutOnDropdownOnChange"
                  />
                </div>
                <label
                  v-if="passThroughBidEnabled"
                  name="conversionType-disabled-label"
                  data-cy="conversionType-disabled-label"
                  class="p-t-7 text-muted"
                >{{ $t('LABEL_TARGET_ROUTE_OVERRIDE') }}
                </label>
              </ValidationProvider>
            </template>
          </Field>

          <div v-if="!passThroughBidEnabled && conversionType == 'Call Length'">
            <Field
              :label="$t('LABEL_START_TIMER_EVENT')"
              :hide-label="false"
              :has-tooltip="true"
              :tooltip-text="$t('TOOLTIP_START_TIMER_EVENT')"
            >
              <template #element>
                <ValidationProvider
                  :rules="passThroughBidEnabled ? '' : 'required'"
                  slim
                >
                  <MultipleToggle
                    :value="startTimerEvent"
                    :options="startTimerEventOptions"
                    @onToggle="onStartTimerEventChange"
                  />
                </ValidationProvider>
              </template>
            </Field>

            <Field
              :label="$t('LABEL_LENGTH')"
              :hide-label="false"
              :has-tooltip="true"
              :tooltip-text="$t('TOOLTIP_LENGTH')"
            >
              <template #element>
                <ValidationProvider
                  v-slot="{ classes, errors }"
                  :rules="passThroughBidEnabled ? '' : 'required|min_value:0|integer'"
                  slim
                >
                  <div class="p-l-0 col-sm-5 col-md-1">
                    <input
                      :value="callLengthInSeconds"
                      name="currentCallLengthInSeconds"
                      data-cy="currentCallLengthInSeconds"
                      :required="true"
                      :placeholder="$t('LABEL_PLACEHOLDER_LENGTH')"
                      class="form-control"
                      :class="classes"
                      type="number"
                      @input="onCallLengthInSecondsOnInput"
                      @change="onCallLengthInSecondsOnChange"
                      @wheel="$event.target.blur()"
                    >
                  </div>
                  <label class="m-t-10">seconds</label>
                  <label
                    v-if="errors[0]"
                    class="form-group__label-required vv-invalid"
                    data-cy="currentCallLengthInSeconds-error-label"
                  >Length must be a number and greater or equal to zero
                  </label>
                  <label
                    v-else
                    class="form-group__label-required p-l-10"
                  >{{ $t('REQUIRED') }}
                  </label>
                </ValidationProvider>
              </template>
            </Field>
          </div>

          <Field
            :label="$t('LABEL_DUPLICATE_PAYOUTS')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_DUPLICATE_PAYOUTS')"
          >
            <template #element>
              <div class="p-l-0 col-sm-6 col-md-6">
                <ValidationProvider
                  rules="required"
                  slim
                >
                  <MultipleToggle
                    :value="secondsFromLastCallToggleValue"
                    :options="secondsFromLastCallToggleValueOptions"
                    @onToggle="onSecondsFromLastCallToggleValueChange"
                  >
                    <template>
                      <div
                        v-if="secondsFromLastCallToggleValue > 0"
                        class="m-t-5"
                      >
                        <ValidationProvider
                          rules="required"
                          slim
                        >
                          <input
                            v-model.lazy="daysFromLastCall"
                            class="form-control dynamic-inbound-calls-timeout-input"
                            type="number"
                            min="0"
                            oninput="validity.valid||(value='')"
                            @wheel="$event.target.blur()"
                          >
                        </ValidationProvider>
                        <label class="dynamic-inbound-calls-timeout-labels">{{ daysFromLastCall > 1 ? 'Days' : 'Day' }}</label>
                        <ValidationProvider
                          rules="required"
                          slim
                        >
                          <input
                            v-model.lazy="hoursFromLastCall"
                            class="form-control dynamic-inbound-calls-timeout-input"
                            type="number"
                            min="0"
                            oninput="validity.valid||(value='')"
                            @wheel="$event.target.blur()"
                          >
                        </ValidationProvider>
                        <label class="dynamic-inbound-calls-timeout-labels">{{ hoursFromLastCall > 1 ? 'Hours' : 'Hour' }}</label>
                      </div>
                    </template>
                  </MultipleToggle>
                </ValidationProvider>
              </div>
            </template>
          </Field>
        </div>

      <!-- Payout Hours and Limit Payouts will not be avaialble on v1-->
      <!-- <Field
        :label="$t('LABEL_PAYOUT_HOURS')"
        :hideLabel="false"
        :has-tooltip="true"
        :tooltip-text="$t('TOOLTIP_PAYOUT_HOURS')"
      >
        <template #element>
          <nzToggle 
            v-model="payoutHoursToggleval"
            @onToggle="onPayoutHoursToggle" />
        </template>
      </Field>

      <Field
        :label="$t('LABEL_LIMIT_PAYOUT')"
        :hideLabel="false"
        :has-tooltip="true"
        :tooltip-text="$t('TOOLTIP_LIMIT_PAYOUT')"
      >
        <template #element>
          <nzToggle 
            v-model="limitPayoutToggleval"
            @onToggle="onLimitPayoutToggle" />
        </template>
      </Field> -->
      <!-- Payout Hours and Limit Payouts will not be avaialble on v1-->
      </form>
    </template>
  </SectionHeader>
</template>

<script>
import messages from '../locales';
import sharedMessages from '@/core/locales';

import SectionHeader from '@/core/components/SectionHeader/SectionHeader';
import Field from '@/core/components/LabelAndElement/LabelAndElement';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import MultipleToggle from '@/core/components/MultipleToggle/MultipleToggle';
import nzToggle from '@/core/components/nzToggle/nzToggle';

export default {
  name: 'DefaultBidSettings',
  components: { SectionHeader, Field, SearchableDropdown, MultipleToggle, nzToggle },
  props:{
    baseBidAmount: {
      type: String,
      default: () => {
        return '';
      }
    },
    maxBidAmount: {
      type: String,
      default: () => {
        return '';
      }
    },
    minBidAmount: {
      type: String,
      default: () => {
        return '';
      }
    },
    conversionType: {
      type: String,
      default: () => {
        return '';
      }
    },
    conversionTypeDropdownConfig: {
      type: Object,
      default: () => {
        return{
          options: ['Call Length', 'Call Successfully Connected', 'Inbound Call'],
          placeholder: 'Choose Type'
        };
      }
    },
    startTimerEvent: {
      type: String,
      default: () => {
        return null;
      }
    },
    callLengthInSeconds: {
      type: String,
      default: () => {
        return '';
      }
    },
    secondsFromLastCall: {
      type: Number,
      default: () => {
        return -1;
      }
    },
    payoutHoursToggleval: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
    limitPayoutToggleval: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
    overridesAvailable: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
    overrideDefaultBidSettings: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
    currentPassThroughBid: {
      type: Object,
      default: () => {
        return {
          enabled: false,
          allowBidsAtLoss: false
        };
      }
    },
    passThroughBidFeatureFlag: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
  },
  data() {
    return {
      currentBaseBidAmount: this.baseBidAmount ? '$' + this.baseBidAmount : '',
      currentmaxBidAmount: this.maxBidAmount ? '$' + this.maxBidAmount : '',
      currentminBidAmount: this.minBidAmount ? '$' + this.minBidAmount : '',
      currentConversionType: this.conversionType ? this.conversionType : 'Inbound Call',
      currentCallLengthInSeconds: this.callLengthInSeconds,
      defaultBidSettingsShowContents: true,
      baseBidNumericInput: false,
      maxBidAmountNumericInput: false,
      minBidAmountNumericInput: false,
      startTimerEventOptions: Object.freeze([{ name: this.$t('MULTIPLE_TOGGLE_OPTIONS_INCOMING'), value: 'OnIncomingCall' }, { name: this.$t('MULTIPLE_TOGGLE_OPTIONS_DIAL'), value: 'OnDial' }, { name: this.$t('MULTIPLE_TOGGLE_OPTIONS_CONNECT'), value: 'OnCallConnected' }]),
      secondsFromLastCallToggleValueOptions: Object.freeze([{ name: this.$t('MULTIPLE_TOGGLE_OPTIONS_DISABLED'), value: 0 }, { name: this.$t('MULTIPLE_TOGGLE_OPTIONS_ENABLED'), value: -1 }, { name: this.$t('MULTIPLE_TOGGLE_OPTIONS_TIME_LIMIT'), value: 1 }])
    };
  },
  computed: {
    daysFromLastCall: {
      get: function() {
        return Math.floor(this.secondsFromLastCall / 86400);
      },
      set: function(val) {
        if (!val) {
          val = '0';
        }
        this.$emit('onSecondsFromLastCallChange', this._GetSecondsFromDaysAndHours(parseInt(val), this.hoursFromLastCall));
      }
    },
    hoursFromLastCall: {
      get: function() {
        return ((this.secondsFromLastCall % 86400) / 3600);
      },
      set: function(val) {
        if (!val) {
          val = '0';
        }
        this.$emit('onSecondsFromLastCallChange', this._GetSecondsFromDaysAndHours(this.daysFromLastCall, parseInt(val)));
      }
    },
    secondsFromLastCallToggleValue() {
      return this.secondsFromLastCall < 1 ? this.secondsFromLastCall : 1;
    },
    passThroughBidEnabled() {
      if (this.passThroughBidFeatureFlag) {
        return this.currentPassThroughBid && this.currentPassThroughBid.enabled;
      }
      return false;
    },
    passThroughBidEnabledAllowErrors() {
      if (this.passThroughBidFeatureFlag) {
        return this.currentPassThroughBid && !this.currentPassThroughBid.enabled;
      }
      return true;
    },
  },
  watch: {
    passThroughBidEnabled: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          if (!this.currentBaseBidAmount){
            this.currentBaseBidAmount = 10;
            this.baseBidNumericInput = true;
          }
          if (!this.currentmaxBidAmount) {
            this.currentmaxBidAmount = 25;
            this.maxBidAmountNumericInput = true;
          }
        }
      }
    }
  },
  methods: {
    overrideDefaultBidSettingsToggle(val) {
      this.$emit('overrideDefaultBidSettingsToggle', val);
    },
    onDefaultBidSettingsShowContentsClick() {
      this.defaultBidSettingsShowContents = !this.defaultBidSettingsShowContents;
    },
    onBaseBidAmountOnInput(val) {
      this.$emit('onBaseBidAmountOnInput', val);
    },
    onBaseBidAmountOnChange(val) {
      this.$emit('onBaseBidAmountOnChange', val);
    },
    onmaxBidAmountOnInput(val) {
      this.$emit('onmaxBidAmountOnInput', val);
    },
    onmaxBidAmountOnChange(val) {
      this.$emit('onmaxBidAmountOnChange', val);
    },
    onminBidAmountOnInput(val) {
      this.$emit('onminBidAmountOnInput', val);
    },
    onminBidAmountOnChange(val) {
      this.$emit('onminBidAmountOnChange', val);
    },
    onPayoutOnDropdownOnChange(newDropdownValue) {
      this.$emit('onPayoutOnDropdownOnChange', newDropdownValue);
    },
    onStartTimerEventChange(val) {
      this.$emit('onStartTimerEventChange', val);
    },
    onSecondsFromLastCallToggleValueChange(val) {
      if (val.value > 0) {
        this.$emit('onSecondsFromLastCallChange', 3600);
      } else {
        this.$emit('onSecondsFromLastCallChange', val.value);
      }
    },
    onCallLengthInSecondsOnInput(val) {
      this.$emit('onCallLengthInSecondsOnInput', val);
    },
    onCallLengthInSecondsOnChange(val) {
      this.$emit('onCallLengthInSecondsOnChange', val);
    },
    onPayoutHoursToggle(val){
      this.$emit('onPayoutHoursToggle', val);
    },
    onLimitPayoutToggle(val){
      this.$emit('onLimitPayoutToggle', val);
    },
    _GetSecondsFromDaysAndHours(days, hours) {
      return (days * 86400) + (hours * 3600);
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  },
};
</script>

<style lang="scss" scoped>
.dynamic-inbound-calls-timeout-label {
  margin-left: 3%;
}
.dynamic-inbound-calls-timeout-input {
  margin-left: 3%;
  width: 10%;
}
.dynamic-inbound-calls-basebid-and-maxBidAmount-input, .dynamic-inbound-calls-basebid-and-minBidAmount-input {
  text-align: right;
  min: "0" !important;
}
</style>